import { queryOptions } from '@tanstack/react-query'
import { apiGet, unwrap } from '@/shared/api'
import {
  type Gantt,
  GanttSchema,
  type LinkedTask,
  LinkedTaskSchema,
  type SearchHit,
  SearchHitSchema,
  type TaskDependencies,
  TaskDependenciesSchema,
} from '../model/relations'
import { taskKeys } from './queries'

interface GanttDto {
  board_id: string
  tasks?: {
    id: string
    title: string
    due_date?: string
    column_id: string
    assignee_id?: string
    blocked: boolean
    waiting: boolean
  }[]
  dependencies?: { task_id: string; depends_on_id: string }[]
}

interface LinkedTaskDto {
  id: string
  title: string
  board_id: string
}

interface DependencyTaskDto {
  id: string
  title: string
  board_id: string
  done: boolean
}

interface TaskDependenciesDto {
  blockers?: DependencyTaskDto[]
  dependents?: DependencyTaskDto[]
}

interface HitDto {
  kind: string
  // entity_id канона; id — его алиас. Для task/message = id задачи.
  entity_id?: string
  id?: string
  label?: string
  title?: string
  snippet?: string
  matched_in: string
  url: string
  task_id?: string
  board_id?: string
  board_name?: string
  project_id?: string
  project_name?: string
  company_id?: string
  company_name?: string
}

function toGantt(dto: GanttDto): Gantt {
  return GanttSchema.parse({
    boardId: dto.board_id,
    tasks: (dto.tasks ?? []).map((t) => ({
      id: t.id,
      title: t.title,
      dueDate: t.due_date ?? null,
      columnId: t.column_id,
      assigneeId: t.assignee_id ? t.assignee_id : null,
      blocked: t.blocked,
      waiting: t.waiting,
    })),
    dependencies: (dto.dependencies ?? []).map((d) => ({
      taskId: d.task_id,
      dependsOnId: d.depends_on_id,
    })),
  })
}

function toLinkedTask(dto: LinkedTaskDto): LinkedTask {
  return LinkedTaskSchema.parse({ id: dto.id, title: dto.title, boardId: dto.board_id })
}

function toDependencyTask(dto: DependencyTaskDto) {
  return { id: dto.id, title: dto.title, boardId: dto.board_id, done: dto.done }
}

function toTaskDependencies(dto: TaskDependenciesDto): TaskDependencies {
  return TaskDependenciesSchema.parse({
    blockers: (dto.blockers ?? []).map(toDependencyTask),
    dependents: (dto.dependents ?? []).map(toDependencyTask),
  })
}

function toHit(dto: HitDto): SearchHit {
  const id = dto.id ?? dto.entity_id ?? dto.task_id ?? ''
  return SearchHitSchema.parse({
    kind: dto.kind,
    id,
    title: dto.title ?? dto.label ?? '',
    snippet: dto.snippet ?? '',
    matchedIn: dto.matched_in,
    url: dto.url,
    taskId: dto.task_id ?? '',
    boardId: dto.board_id ?? '',
    boardName: dto.board_name ?? '',
    projectId: dto.project_id ?? '',
    projectName: dto.project_name ?? '',
    companyId: dto.company_id ?? '',
    companyName: dto.company_name ?? '',
  })
}

// Gantt-проекция доски: сроки + зависимости + флаги риска. Источник «Ждёт»/
// «Блокирует» для карточки (фильтруем рёбра по id задачи).
export function boardGanttQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: taskKeys.gantt(boardId),
    queryFn: async (): Promise<Gantt> =>
      toGantt(unwrap<GanttDto>(await apiGet(`/boards/${boardId}/gantt`))),
  })
}

// Зависимости задачи (blockers/dependents) — надёжный источник «Ждёт»/«Блокирует»,
// включая cross-board контрагентов; done резолвится бэком.
export function taskDependenciesQueryOptions(taskId: string) {
  return queryOptions({
    queryKey: taskKeys.dependencies(taskId),
    queryFn: async (): Promise<TaskDependencies> =>
      toTaskDependencies(
        unwrap<TaskDependenciesDto>(await apiGet(`/tasks/${taskId}/dependencies`)),
      ),
  })
}

// Симметричные связи «Связано» задачи (другая сторона пар).
export function taskLinksQueryOptions(taskId: string) {
  return queryOptions({
    queryKey: taskKeys.links(taskId),
    queryFn: async (): Promise<LinkedTask[]> =>
      (unwrap<LinkedTaskDto[]>(await apiGet(`/tasks/${taskId}/links`)) ?? []).map(toLinkedTask),
  })
}

// Поиск задач (GET /api/search, канон 00-architecture § 6): текстовый запрос +
// опциональный tag-фильтр (уточняет выдачу, не заменяет запрос — бэк требует
// непустой q, SEARCH_EMPTY_QUERY). Пикер связей зовёт без тега; страница поиска —
// с тегом (sticky-фильтр по клику на чип задачи).
export function searchTasksQueryOptions(query: string, tag?: string) {
  const q = query.trim()
  const t = tag?.trim() || undefined
  return queryOptions({
    queryKey: taskKeys.search(q, t ?? null),
    queryFn: async (): Promise<SearchHit[]> =>
      (
        unwrap<HitDto[]>(await apiGet('/search', { q, limit: '20', ...(t ? { tag: t } : {}) })) ??
        []
      ).map(toHit),
    enabled: q.length > 0,
  })
}
